import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Star, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Calendar, 
  Users, 
  Award, 
  Shield, 
  CheckCircle, 
  Clock, 
  BookOpen, 
  TrendingUp, 
  Eye, 
  Heart, 
  Share2, 
  ExternalLink, 
  ArrowLeft, 
  Loader2, 
  Briefcase, 
  UserCheck, 
  FileText, 
  Download,
  ChevronRight,
  ChevronDown,
  MessageCircle,
  ThumbsUp,
  X,
  XCircle,
  Facebook,
  Linkedin,
  Building2,
  Target,
  Zap,
  Play,
  Filter,
  ChevronUp,
  User,
  GraduationCap,
  DollarSign,
  Clock3,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { consultantsService, Consultant, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import { useAuth } from '../../context/AuthContext';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { API_ENDPOINTS } from '../../config/api';

// Animation variants for modern landing page effects
const fadeUpVariants: any = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1
  }
};

const staggerContainer: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1
  }
};

const slideInVariants: any = {
  hidden: { 
    opacity: 0, 
    x: -50,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1
  }
};

const scaleVariants: any = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
    y: 20
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0
  }
};

const floatingVariants: any = {
  animate: {
    y: [-10, 10, -10]
  }
};

// Scroll reveal component
const ScrollReveal: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { 
          opacity: 1, 
          y: 0
        }
      }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const ServiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const [service, setService] = useState<ConsultantService | null>(null);
  const [consultant, setConsultant] = useState<Consultant | null>(null);
  const [allServices, setAllServices] = useState<ConsultantService[]>([]);
  const [creneaux, setCreneaux] = useState<Creneau[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingLoading, setBookingLoading] = useState<number | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);
  const [showBookingConfirm, setShowBookingConfirm] = useState<boolean>(false);
  const [creneauToBook, setCreneauToBook] = useState<Creneau | null>(null);
  const trackedServiceIdRef = useRef<string | null>(null);
  const isTrackingRef = useRef<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    description: false,
    objectives: false,
    skills: false,
    audience: false
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (id) {
      // Reset tracking state when service ID changes
      if (trackedServiceIdRef.current !== id) {
        trackedServiceIdRef.current = null;
        isTrackingRef.current = false;
      }
      loadServiceData();
    }
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const loadServiceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const serviceData = await consultantsService.getService(id!);
      setService(serviceData);

      // Track view only once per service visit (avoid double counting)
      // Check both if this service ID was already tracked AND if we're not currently tracking
      const serviceId = id!;
      if (trackedServiceIdRef.current !== serviceId && !isTrackingRef.current) {
        isTrackingRef.current = true;
        try {
          await trainingsService.trackTrainingView(serviceId);
          trackedServiceIdRef.current = serviceId; // Mark this service ID as tracked
        } catch (viewError) {
          // Reset tracking flag on error so it can be retried if needed
          trackedServiceIdRef.current = null;
        } finally {
          isTrackingRef.current = false;
        }
      }

      // Load consultant data
      if (serviceData.training_center_id) {
        try {
          const consultantData = await consultantsService.getConsultant(serviceData.training_center_id);
          setConsultant(consultantData);

          // Load all services from this consultant
          const servicesData = await consultantsService.getAllServices({ consultant_id: serviceData.training_center_id });
          setAllServices(servicesData.services);
        } catch (err) {
        }
      }

      // Load creneaux
      try {
        const creneauxData = await creneauxService.getCreneauxByService(id!);
        setCreneaux(creneauxData);
      } catch (err) {
        setCreneaux([]);
      }

    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const isCreneauBooked = (creneau: Creneau) => {
    return creneau.is_booked || creneau.booking_status === 'approved';
  };

  const canBookCreneau = (creneau: Creneau) => {
    // Allow booking if available, not booked, and not approved
    // Rejected créneaux become available again for booking
    return creneau.is_available && 
           !creneau.is_booked && 
           creneau.booking_status !== 'approved';
  };

  const isCreneauPending = (creneau: Creneau) => {
    return creneau.booking_status === 'pending';
  };

  const getCreneauStatus = (creneau: Creneau) => {
    if (creneau.booking_status === 'pending') {
      return { text: t('consultant.calendar.pending'), color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    } else if (creneau.booking_status === 'approved' || creneau.is_booked) {
      return { text: t('consultant.calendar.booked'), color: 'bg-red-100 text-red-700 border-red-200' };
    } else if (creneau.booking_status === 'rejected') {
      return { text: t('public.serviceDetail.rejected'), color: 'bg-gray-100 text-gray-700 border-gray-200' };
    } else {
      return { text: t('consultant.calendar.available'), color: 'bg-green-100 text-green-700 border-green-200' };
    }
  };

  const handleBookCreneauClick = (creneau: Creneau) => {
    if (!isAuthenticated) {
      navigate('/auth/user', { state: { from: `/service/${id}` } });
      return;
    }
    setCreneauToBook(creneau);
    setShowBookingConfirm(true);
  };

  const handleConfirmBooking = async () => {
    if (!creneauToBook) return;

    try {
      setBookingLoading(creneauToBook.id);
      setBookingError(null);
      setBookingSuccess(null);
      setShowBookingConfirm(false);

      await creneauxService.bookCreneauForCurrentUser(creneauToBook.id);
      
      setBookingSuccess(t('public.serviceDetail.bookingSuccess'));
      
      // Refresh creneaux
      const creneauxData = await creneauxService.getCreneauxByService(id!);
      setCreneaux(creneauxData);
      
      setTimeout(() => {
        setBookingSuccess(null);
      }, 3000);
      
    } catch (error: any) {
      setBookingError(error.response?.data?.message || t('common.error'));
      
      setTimeout(() => {
        setBookingError(null);
      }, 5000);
    } finally {
      setBookingLoading(null);
      setCreneauToBook(null);
    }
  };

  const handleCancelBooking = () => {
    setShowBookingConfirm(false);
    setCreneauToBook(null);
  };

  const availableCreneaux = creneaux.filter(c => canBookCreneau(c));
  const pendingCreneaux = creneaux.filter(c => isCreneauPending(c));

  // Get similar services (excluding current service, limit to 2)
  const similarServices = allServices
    .filter(s => s.id !== service?.id)
    .slice(0, 2);

  // Simple service list item for sidebar
  const ServiceListItem = ({ s }: { s: ConsultantService }) => {
    const isActive = s.id === service?.id;
    
    return (
      <motion.div
        onClick={() => navigate(`/service/${s.id}`)}
        className={`bg-white rounded-lg sm:rounded-xl border-2 overflow-hidden cursor-pointer transition-all duration-300 group touch-manipulation ${
          isActive 
            ? 'border-brand-primary shadow-lg' 
            : 'border-gray-200 hover:border-brand-primary/30 hover:shadow-md'
        }`}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.98 }}
      >
        {s.banner_image ? (
          <div className="h-20 sm:h-24 overflow-hidden">
            <img
              src={s.banner_image.startsWith('http') ? s.banner_image : `${API_ENDPOINTS.UPLOADS}/${s.banner_image}`}
              alt={s.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
              }}
            />
          </div>
        ) : (
          <div className="h-20 sm:h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 flex items-center justify-center">
            <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-brand-primary/50" />
          </div>
        )}
        
        <div className="p-2.5 sm:p-3">
          <h4 className={`text-xs sm:text-sm font-bold mb-1 line-clamp-2 transition-colors break-words ${
            isActive ? 'text-brand-primary' : 'text-gray-900 group-hover:text-brand-primary'
          }`}>
            {s.title}
          </h4>
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-gray-500">
            <Clock3 className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{s.estimated_duration || t('public.serviceDetail.flexible', 'Flexible')}</span>
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary/20"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-primary border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p className="text-gray-600 mt-4 text-lg font-medium">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-brand-surface flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-12 w-12 text-brand-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('common.error')}</h2>
          <p className="text-gray-600 mb-8 text-lg">{error || t('public.serviceDetail.notFound')}</p>
          <motion.button
            onClick={() => navigate(-1)}
            className="bg-gradient-to-r from-brand-primary to-purple-600 text-white px-8 py-3 rounded-xl hover:from-purple-600 hover:to-brand-primary transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            {t('common.back')}
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface pb-8">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4 sm:py-5 md:py-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 sm:gap-3 text-gray-600 hover:text-brand-primary transition-colors font-medium text-sm sm:text-base touch-manipulation min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">{t('common.back')}</span>
            </button>
            
            <div className="flex items-center gap-2 sm:gap-4">
              <button className="p-2 sm:p-3 text-gray-600 hover:text-brand-secondary transition-colors hover:bg-brand-secondary/5 rounded-xl touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center">
                <Share2 className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 pb-12 sm:pb-16">
        {/* Booking Confirmation Modal */}
        {showBookingConfirm && creneauToBook && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={handleCancelBooking}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-5 md:p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-brand-primary to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                    {t('public.serviceDetail.confirmBooking')}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    {t('public.serviceDetail.confirmBookingMessage')}
                  </p>
                </div>
              </div>

              {/* Créneau Details */}
              <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-start sm:items-center gap-2">
                    <Calendar className="h-4 w-4 text-brand-primary flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                      {new Date(creneauToBook.creneau_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-brand-secondary flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      {creneauToBook.start_time} - {creneauToBook.end_time}
                    </span>
                  </div>
                  {service && (
                    <div className="flex items-start sm:items-center gap-2">
                      <Briefcase className="h-4 w-4 text-brand-accent flex-shrink-0 mt-0.5 sm:mt-0" />
                      <span className="text-xs sm:text-sm text-gray-700 break-words">{service.title}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleCancelBooking}
                  className="flex-1 px-4 py-3 sm:py-2.5 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base touch-manipulation min-h-[44px]"
                >
                  {t('common.cancel', 'Annuler')}
                </button>
                <motion.button
                  onClick={handleConfirmBooking}
                  disabled={bookingLoading === creneauToBook.id}
                  className="flex-1 px-4 py-3 sm:py-2.5 bg-gradient-to-r from-brand-primary to-purple-600 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-brand-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-manipulation min-h-[44px]"
                  whileHover={{ scale: bookingLoading === creneauToBook.id ? 1 : 1.02 }}
                  whileTap={{ scale: bookingLoading === creneauToBook.id ? 1 : 0.98 }}
                >
                  {bookingLoading === creneauToBook.id ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-xs sm:text-sm">{t('common.processing', 'Traitement...')}</span>
                    </div>
                  ) : (
                    t('public.serviceDetail.confirm')
                  )}
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Success/Error Messages */}
        {bookingSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4 sm:mb-6 bg-green-100 border border-green-400 text-green-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 text-sm sm:text-base"
          >
            <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="break-words">{bookingSuccess}</span>
          </motion.div>
        )}
        
        {bookingError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-4 sm:mb-6 bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 text-sm sm:text-base"
          >
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="break-words">{bookingError}</span>
          </motion.div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Hero Section */}
            <motion.div 
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8"
              initial="hidden"
              animate="visible"
              variants={fadeUpVariants}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                staggerChildren: 0.1
              }}
            >
              {/* Banner Image */}
              <div className="h-48 sm:h-64 md:h-80 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 relative overflow-hidden">
                {/* Animated background gradient */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-brand-primary/5 via-transparent to-brand-secondary/5"
                  animate={{
                    background: [
                      "linear-gradient(45deg, rgba(139, 69, 19, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(236, 72, 153, 0.05) 100%)",
                      "linear-gradient(45deg, rgba(236, 72, 153, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(139, 69, 19, 0.05) 100%)",
                      "linear-gradient(45deg, rgba(139, 69, 19, 0.05) 0%, rgba(0, 0, 0, 0) 50%, rgba(236, 72, 153, 0.05) 100%)"
                    ]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                {service.banner_image ? (
                  <motion.img
                    src={service.banner_image.startsWith('http') ? service.banner_image : `${API_ENDPOINTS.UPLOADS}/${service.banner_image}`}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop';
                    }}
                  />
                ) : (
                  <motion.div 
                    className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <motion.div 
                      className="text-center"
                      variants={floatingVariants}
                      animate="animate"
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Briefcase className="h-20 w-20 text-brand-primary mx-auto mb-4" />
                      <div className="text-lg font-semibold text-brand-primary">{t('public.serviceDetail.title', 'Service Details')}</div>
                    </motion.div>
                  </motion.div>
                )}
                
                {/* Views Badge */}
                <motion.div 
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6"
                  initial={{ opacity: 0, x: 30, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 shadow-lg border border-white/20">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-purple-600" />
                      <span className="font-bold text-gray-900 text-xs sm:text-sm md:text-base lg:text-lg">
                        {service.views || 0}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600 font-medium hidden sm:inline">
                        {t('common.views', 'vues')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 md:p-8">
                <motion.div 
                  className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6 gap-4"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.15,
                    delayChildren: 0.2
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <motion.h1 
                      className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 leading-tight break-words"
                      variants={fadeUpVariants}
                    >
                      {service.title}
                    </motion.h1>
                    {consultant && (
                      <motion.div 
                        className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4"
                        variants={slideInVariants}
                      >
                        <motion.div 
                          className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </motion.div>
                        <div className="min-w-0 flex-1">
                          <p className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 break-words">
                            {consultant.name}
                          </p>
                          <p className="text-sm sm:text-base text-gray-600 break-words">
                            {consultant.city && typeof consultant.city === 'string' 
                              ? consultant.city 
                              : consultant.wilaya && typeof consultant.wilaya === 'object' && consultant.wilaya !== null && 'name' in consultant.wilaya
                              ? consultant.wilaya.name
                              : typeof consultant.wilaya === 'string'
                              ? consultant.wilaya
                              : t('public.serviceDetail.online', 'En ligne')}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Meta Information */}
                <motion.div 
                  className="mb-4 sm:mb-6 md:mb-8 flex flex-wrap gap-2 sm:gap-3"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                  transition={{
                    staggerChildren: 0.1,
                    delayChildren: 0.3
                  }}
                >
                  {service.format && (
                    <motion.span 
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-brand-secondary to-pink-600 text-white text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <BookOpen className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="whitespace-nowrap">{service.format}</span>
                    </motion.span>
                  )}
                  {service.language && (
                    <motion.span 
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-brand-mint to-teal-600 text-white text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Globe className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="whitespace-nowrap">{service.language}</span>
                    </motion.span>
                  )}
                  {service.reference && (
                    <motion.span 
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-brand-accent to-yellow-600 text-white text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl shadow-md"
                      variants={scaleVariants}
                      whileHover={{ scale: 1.05, y: -2 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="whitespace-nowrap">Ref: {service.reference}</span>
                    </motion.span>
                  )}
                </motion.div>

                {/* Consultant Names */}
                {service.consultant_names && (
                  <div className="mb-4 sm:mb-6 p-4 sm:p-5 md:p-6 bg-gradient-to-br from-brand-primary/5 to-purple-50 border border-brand-primary/10 rounded-xl sm:rounded-2xl">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <User className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      {t('public.serviceDetail.consultants', 'Consultants')}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                      {service.consultant_names}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Description Section - Collapsible */}
            {service.full_description && (
              <ScrollReveal delay={0.1}>
                <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                  <button
                    onClick={() => toggleSection('description')}
                    className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg sm:rounded-xl touch-manipulation min-h-[44px]"
                  >
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                      <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary flex-shrink-0" />
                      <span className="break-words">{t('public.serviceDetail.description', 'Description')}</span>
                    </h2>
                    {expandedSections.description ? (
                      <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedSections.description && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          {service.full_description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Learning Objectives - Collapsible */}
            {service.learning_objectives && (
              <ScrollReveal delay={0.2}>
                <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                  <button
                    onClick={() => toggleSection('objectives')}
                    className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg sm:rounded-xl touch-manipulation min-h-[44px]"
                  >
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Target className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                      <span className="break-words">{t('training.objectives.title')}</span>
                    </h2>
                    {expandedSections.objectives ? (
                      <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedSections.objectives && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          {service.learning_objectives}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Skills Gained - Collapsible */}
            {service.skills_gained && (
              <ScrollReveal delay={0.3}>
                <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                  <button
                    onClick={() => toggleSection('skills')}
                    className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg sm:rounded-xl touch-manipulation min-h-[44px]"
                  >
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                      <span className="break-words">{t('training.skills.title')}</span>
                    </h2>
                    {expandedSections.skills ? (
                      <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedSections.skills && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          {service.skills_gained}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Target Audience - Collapsible */}
            {service.target_audience && (
              <ScrollReveal delay={0.4}>
                <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6">
                  <button
                    onClick={() => toggleSection('audience')}
                    className="w-full p-3 sm:p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg sm:rounded-xl touch-manipulation min-h-[44px]"
                  >
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Users className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 flex-shrink-0" />
                      <span className="break-words">{t('training.audience.title')}</span>
                    </h2>
                    {expandedSections.audience ? (
                      <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {expandedSections.audience && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                      <div className="prose prose-gray max-w-none">
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                          {service.target_audience}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            {/* Available Créneaux Section */}
            <ScrollReveal delay={0.5}>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 sm:mb-6 p-4 sm:p-5 md:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <span className="break-words">{t('public.serviceDetail.allAvailableSlots')}</span>
                </h2>

                {/* Pending Créneaux */}
                {pendingCreneaux.length > 0 && (
                  <div className="mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 flex-wrap">
                      <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600 flex-shrink-0" />
                      <span className="break-words">{t('public.serviceDetail.pendingReservations', 'Réservations en attente')} ({pendingCreneaux.length})</span>
                    </h3>
                    <div className="space-y-2 sm:space-y-3 max-h-64 overflow-y-auto">
                      {pendingCreneaux.map((creneau) => (
                        <div 
                          key={creneau.id} 
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg gap-2 sm:gap-0"
                        >
                          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-500 rounded-lg flex items-center justify-center shadow-md flex-shrink-0">
                              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-medium text-gray-900 break-words">
                                {new Date(creneau.creneau_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </p>
                              <p className="text-xs text-gray-600">
                                {creneau.start_time} - {creneau.end_time}
                              </p>
                            </div>
                          </div>
                          <span className="px-2.5 sm:px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-lg border border-yellow-200 whitespace-nowrap self-start sm:self-auto">
                            {t('public.serviceDetail.pending', 'En attente')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Available Créneaux */}
                {availableCreneaux.length > 0 ? (
                  <div className={pendingCreneaux.length > 0 ? 'border-t border-gray-200 pt-4 sm:pt-6 mt-4 sm:mt-6' : ''}>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 flex-wrap">
                      <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-brand-primary flex-shrink-0" />
                      <span className="break-words">{t('consultant.calendar.available', 'Disponibles')} ({availableCreneaux.length})</span>
                    </h3>
                    <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
                      {availableCreneaux.map((creneau) => (
                        <div 
                          key={creneau.id} 
                          className="group relative overflow-hidden bg-gradient-to-r from-white to-gray-50 border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all duration-300 hover:border-brand-primary/30"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                            <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-brand-primary transition-colors break-words">
                                  {new Date(creneau.creneau_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </p>
                                <p className="text-xs text-gray-600">
                                  {creneau.start_time} - {creneau.end_time}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                              {canBookCreneau(creneau) && (
                                <motion.button
                                  onClick={() => handleBookCreneauClick(creneau)}
                                  disabled={bookingLoading === creneau.id}
                                  className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gradient-to-r from-brand-primary to-purple-600 text-white text-xs sm:text-sm font-semibold rounded-lg hover:from-purple-600 hover:to-brand-primary transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[44px] sm:min-h-0"
                                  whileHover={{ scale: bookingLoading === creneau.id ? 1 : 1.05 }}
                                  whileTap={{ scale: bookingLoading === creneau.id ? 1 : 0.95 }}
                                >
                                  {bookingLoading === creneau.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin mx-auto sm:mx-0" />
                                  ) : (
                                    t('consultant.calendar.book')
                                  )}
                                </motion.button>
                              )}
                            </div>
                          </div>
                          {/* Animated background effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/5 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Calendar className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                    <p className="text-xs sm:text-sm text-gray-500 px-2">
                      {t('public.serviceDetail.noCreneauxAvailable')}
                    </p>
                  </div>
                )}

                {/* Detailed Program PDF */}
                {service.detailed_program_pdf && (
                  <div className="mt-4 sm:mt-6 border-t border-gray-200 pt-4 sm:pt-6">
                    <a
                      href={service.detailed_program_pdf.startsWith('http') ? service.detailed_program_pdf : `${API_ENDPOINTS.UPLOADS}/${service.detailed_program_pdf}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 w-full justify-center px-3 sm:px-4 py-2.5 sm:py-3 border border-emerald-600 text-emerald-600 font-semibold rounded-lg hover:bg-emerald-50 transition-colors text-xs sm:text-sm touch-manipulation min-h-[44px]"
                    >
                      <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                      <span className="break-words">{t('training.downloadProgram')}</span>
                      <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    </a>
                  </div>
                )}
              </div>
            </ScrollReveal>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">

             {/* Contact Card */}
             {consultant && (
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <span className="break-words">{t('common.contact')}</span>
                </h3>
                
                <div className="space-y-3 sm:space-y-4">
                  {consultant.phone_number && (
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <a href={`tel:${consultant.phone_number}`} className="font-medium text-gray-700 hover:text-brand-primary transition-colors break-all">
                        {consultant.phone_number}
                      </a>
                    </div>
                  )}

                  {consultant.user?.email && (
                    <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-secondary to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <a href={`mailto:${consultant.user.email}`} className="font-medium text-gray-700 hover:text-brand-primary transition-colors break-all">
                        {consultant.user.email}
                      </a>
                    </div>
                  )}

                  {consultant.address && (
                    <div className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-accent to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                      </div>
                      <span className="font-medium text-gray-700 break-words flex-1 min-w-0">{consultant.address}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                  <motion.button
                    onClick={() => navigate(`/consultant/${consultant.id}`)}
                    className="w-full bg-gradient-to-r from-brand-primary to-purple-600 text-white py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl font-semibold hover:from-purple-600 hover:to-brand-primary transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-sm sm:text-base touch-manipulation min-h-[44px]"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('public.serviceDetail.viewConsultantProfile')}
                  </motion.button>
                </div>
              </div>
            )}

            {/* Training Domain Card */}
            {service && (service.domaine || service.subcategory) && (
              <ScrollReveal delay={0.2}>
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <span className="break-words">{t('public.serviceDetail.trainingDomain')}</span>
                  </h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {service.domaine && service.domaine.name_french && service.domaine !== null && (
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-primary to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Target className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 mb-1 font-medium">{t('public.serviceDetail.domain')}</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                            {service.domaine.name_french}
                          </p>
                        </div>
                      </div>
                    )}

                    {service.subcategory && service.subcategory.name_french && (
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 mb-1 font-medium">{t('public.serviceDetail.subcategory')}</p>
                          <p className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                            {service.subcategory.name_french}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            )}

            {/* Similar Services List */}
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-brand-mint to-teal-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <span className="break-words">{t('public.serviceDetail.similarServices')}</span>
              </h3>
              
              <div className="space-y-2 sm:space-y-3">
                {similarServices.length > 0 ? (
                  similarServices.map((s) => (
                    <ServiceListItem key={s.id} s={s} />
                  ))
                ) : (
                  <div className="text-center py-6 sm:py-8">
                    <Briefcase className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                    <p className="text-xs sm:text-sm text-gray-500 px-2">{t('public.serviceDetail.noSimilarServices')}</p>
                  </div>
                )}
              </div>
            </div>

           
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;

